import { useEffect, useCallback, useReducer } from "react";
import api from "lib/api";
import { useDispatch } from "react-redux";
import {
	removeForm,
	save as saveForm,
	mergeUpdate,
	statusError,
	statusUpdating,
} from "data/form/formActions";
import { useHistory } from "react-router";

import TaskReducer, {
	defaultState,
	clearValidationErrorsAction,
	formSubmittingAction,
	fetchAction,
	validationErrorAction,
	jobDoneAction,
	handleTaskResponse,
	errorAction,
	taskReadyAction,
	updateTitleAction,
} from "./useActivityState";

import CustomError from "lib/api/CustomError";
import { statusReady } from "../data/form/formActions";
import { useTranslation } from "react-i18next";
import { createAlertAction } from "data/app/appActions";
import { createErrorAlert, getErrorMessageOrDefault } from "lib/api/errors";
import normaliseForm from "lib/normalisers/form";
import { AxiosError } from "axios";

function useTask(tid: string) {
	const history = useHistory();
	const reduxDispatch = useDispatch();
	const [state, dispatch] = useReducer(TaskReducer, defaultState());
	const { t } = useTranslation();

	const loadTask = useCallback(async () => {
		if (tid !== "0") {
			try {
				const task = await api.workflow.getTask(tid);
				if (!task || !task.data.data[0]?.formInstanceId) {
					throw new Error();
				}
				dispatch(fetchAction(handleTaskResponse(task)));
				dispatch(taskReadyAction());
			} catch (error) {
				const e = error as AxiosError;
				const errorMessage: string | undefined =
					e.response?.data?.message;

				dispatch(
					errorAction(
						new CustomError({
							httpCode: e.response?.status,
							dump: e,
							message:
								errorMessage !== undefined &&
								errorMessage !== null &&
								errorMessage.includes("Business Logic Error")
									? errorMessage
									: undefined,
						})
					)
				);
			}
		}
	}, [tid]);

	const close = useCallback(async () => {
		// Remove the form instance from redux
		if (state.fid) reduxDispatch(removeForm(state.fid));

		// Redirect user to homepage
		history.push("/");
	}, [history, reduxDispatch, state.fid]);

	const complete = useCallback(async () => {
		try {
			// Clear any errors
			dispatch(clearValidationErrorsAction());

			//
			dispatch(formSubmittingAction());

			const validated = state.fid
				? await api.forms.validateForm(state.fid)
				: false;

			if (typeof validated == "boolean") {
				// If validated run the complete action
				const res = await api.workflow.completeTask(tid);

				// If no errors then we need to remove the current form from State
				if (state.fid) {
					reduxDispatch(removeForm(state.fid));
					// dispatch(removeFormFromTask());
				}

				// Put the details of the next task into our local state
				if (res) {
					// dispatch(fetchAction(handleTaskResponse(res)));
					history.push(`/task/${res.data.data[0].id}`);
					// dispatch(taskReadyAction());
				}
			} else {
				// If invalid then push data back to form with new feedback
				dispatch(validationErrorAction());
				if (state.fid) {
					reduxDispatch(statusUpdating(state.fid));
					reduxDispatch(mergeUpdate(state.fid, validated));
					reduxDispatch(statusReady(state.fid));
					dispatch(taskReadyAction());
				}
			}
		} catch (error) {
			const e = error as AxiosError;

			// For any other errors we throw up the tasks error screen
			// don't try to recover at this point
			dispatch(
				errorAction(
					new CustomError({
						httpCode: e.response?.status,
						dump: error,
					})
				)
			);
		}
	}, [tid, reduxDispatch, state.fid, history]);

	const save = useCallback(() => {
		if (state.fid)
			reduxDispatch(
				saveForm(state.fid, tid, taskName =>
					dispatch(updateTitleAction(taskName))
				)
			);
	}, [reduxDispatch, state.fid, tid]);

	const saveAndClose = useCallback(async (): Promise<boolean> => {
		let closed = false;
		if (state.fid) {
			const fid = state.fid;
			// Just set the state to updating to indicate to user
			reduxDispatch(statusUpdating(fid));
			await api.forms
				.save(fid, { handleErrors: true })
				.then(async () => {
					await close().then(() => (closed = true));
				})
				.catch(e => {
					const error = e as AxiosError;
					if (error.response && error.response?.status === 400) {
						// If error comes back on save merge the error so it is shown to user
						reduxDispatch(
							mergeUpdate(fid, normaliseForm(error.response.data))
						);
						reduxDispatch(statusReady(fid));
					} else {
						reduxDispatch(
							statusError(fid, getErrorMessageOrDefault(error))
						);
					}
				});
		} else {
			await close().then(() => (closed = true));
		}
		return closed;
	}, [close, reduxDispatch, state.fid]);

	/**
	 * Unassigning a task from the task page
	 */
	const unassignTask = useCallback(async () => {
		try {
			if (state.id) {
				await api.workflow.unassignTask(state.id.toString());
				close();
			}
		} catch (error) {
			const e = error as AxiosError;
			reduxDispatch(
				createAlertAction(
					createErrorAlert(e, "unassignError", {
						id: state.id
							? state.id?.toString()
							: t("alerts.idNotFound"),
					})
				)
			);
		}
	}, [close, reduxDispatch, state.id, t]);

	/**
	 * Deleting an activity from the task page
	 */
	const deleteActivity = useCallback(async () => {
		try {
			if (state.id) {
				await api.workflow.deleteActivity(state.id.toString(), {
					handleErrors: true,
				});
				close();
			}
		} catch (error) {
			const e = error as AxiosError;
			if (e.response || e.message)
				reduxDispatch(
					createAlertAction(
						createErrorAlert(e, "deleteActivityError", {
							id: state.id
								? state.id?.toString()
								: t("alerts.idNotFound"),
						})
					)
				);
		}
	}, [close, reduxDispatch, state.id, t]);

	useEffect(() => {
		if (tid === "0") dispatch(jobDoneAction());
	}, [tid]);

	return {
		complete,
		close,
		state,
		loadTask,
		save,
		saveAndClose,
		unassignTask,
		deleteActivity,
	};
}

export default useTask;
